import { InjectionToken } from '@angular/core';
import { Observable, Subject, switchMap } from 'rxjs';
import { AuthenticatedIdentity, identityKey } from './authentication.service';

/**
 * #34116: the client name of the Software Security push that says "this user's permissions changed, reload
 * them". The SS API `NotificationHub` sends this to the affected user's group (or all clients) when a
 * role / permission / company / environment change is scheduled. Must match the server literal
 * (`NotificationBroadcaster` sends `"userPermissionsChanged"`).
 */
export const PERMISSIONS_CHANGED_EVENT = 'userPermissionsChanged';

/**
 * #34116: a platform-neutral realtime transport that `SecurityService` (which is `platform:agnostic`, shared
 * by web and NativeScript) can use to receive permission-change pushes without importing a platform-specific
 * SignalR client. A platform provides an implementation against {@link SECURITY_REALTIME_CONNECTOR}; when no
 * implementation is provided, `SecurityService` is a no-op and permissions load exactly as before (cache-first
 * with a background refresh at app start).
 *
 * Implementations MUST NOT throw out of {@link start} — a transport failure has to leave sign-in and the
 * initial permission load untouched.
 */
export interface SecurityRealtimeConnector {
  /** Register a handler for a named server push (e.g. {@link PERMISSIONS_CHANGED_EVENT}). */
  on(eventName: string, handler: () => void): void;

  /** Open the connection. Reconnects on drop are the implementation's responsibility. Never throws. */
  start(): Promise<void> | void;

  /** Close the connection. */
  stop(): Promise<void> | void;
}

/**
 * #34116: optional DI token for a {@link SecurityRealtimeConnector}. Inject with `{ optional: true }`; a null
 * value means no realtime transport is configured and the permission-refresh push is simply not wired.
 */
export const SECURITY_REALTIME_CONNECTOR =
  new InjectionToken<SecurityRealtimeConnector>('SECURITY_REALTIME_CONNECTOR');

/** Minimal logger surface {@link RealtimePermissionRefresh} needs (a subset of `LogService`). */
export interface RealtimeWireLogger {
  debug(message: string, ...args: unknown[]): void;
  warn(message: string, ...args: unknown[]): void;
}

/**
 * #34116: drives the realtime permission-change push for one {@link SecurityRealtimeConnector} across the
 * session. Call {@link follow} with every identity emission:
 * - an Auth0 identity starts the connection (the server keys the push group by auth0 id, so an Entra or
 *   InternalAuth identity can never be reached and is never connected);
 * - sign-out (a null or key-less identity) stops it;
 * - a different user stops the old connection, then starts a fresh one, so the hub re-joins the NEW user's
 *   `perm:<auth0Id>` group from the new token.
 * The push handler is registered once and reloads whichever identity is current (latest push wins). Stop/start run in order on a
 * promise chain, and every transport failure (sync throw or rejection) is logged and never propagates, so sign-in
 * and the initial permission load are untouched. No-op when `connector` is null.
 */
export class RealtimePermissionRefresh {
  private handlerRegistered = false;
  private active: AuthenticatedIdentity | null = null;
  private lifecycle: Promise<void> = Promise.resolve();
  private readonly pushes = new Subject<AuthenticatedIdentity>();

  constructor(
    private readonly connector: SecurityRealtimeConnector | null | undefined,
    reload: (identity: AuthenticatedIdentity) => Observable<unknown>,
    private readonly log: RealtimeWireLogger
  ) {
    // Latest push wins: an older in-flight reload is dropped rather than applied after a newer one.
    this.pushes.pipe(switchMap((identity) => reload(identity))).subscribe();
  }

  follow(identity: AuthenticatedIdentity | null): void {
    const connector = this.connector;
    const next =
      identity?.kind === 'auth0' && identityKey(identity) ? identity : null;
    if (!connector || identityKey(next) === identityKey(this.active)) {
      return;
    }
    const wasConnected = this.active !== null;
    this.active = next;
    if (next && !this.registerHandler(connector)) {
      return;
    }
    this.lifecycle = this.lifecycle
      .then(() => (wasConnected ? connector.stop() : undefined))
      .catch((error) => this.warn('failed to stop', error))
      .then(() =>
        next && this.active === next ? connector.start() : undefined
      )
      .catch((error) => this.warn('failed to start', error));
  }

  private registerHandler(connector: SecurityRealtimeConnector): boolean {
    if (this.handlerRegistered) {
      return true;
    }
    try {
      connector.on(PERMISSIONS_CHANGED_EVENT, () => {
        if (!this.active) {
          return;
        }
        this.log.debug(
          'SecurityService: permissions-changed push received; reloading permissions'
        );
        this.pushes.next(this.active);
      });
      this.handlerRegistered = true;
      return true;
    } catch (error) {
      this.warn('could not be wired', error);
      return false;
    }
  }

  private warn(what: string, error: unknown): void {
    this.log.warn(
      `SecurityService: realtime permission-refresh connector ${what}`,
      error
    );
  }
}
